import React, { useState, useEffect } from 'react'
import {
  Plus,
  Search,
  Filter,
  Download,
  Edit,
  Trash2,
  Eye,
  User,
  Building2,
  Phone,
  Mail,
  MapPin
} from 'lucide-react'
import { Cliente, ClienteFilters, DISTRITOS_PORTUGAL } from '../../types'
import { useToast } from '../Toast'
import Modal, { ConfirmModal } from '../Modal'
import clientesService from '../../services/clientes'
import Table from '../ui/Table'
import Pagination from '../ui/Pagination'
import Input from '../ui/Input'
import Select from '../ui/Select'
import Button from '../ui/Button'
import ClienteModal from './ClienteModal'
import ClienteDetails from './ClienteDetails'

interface ClientesListProps {
  onClienteSelect?: (cliente: Cliente) => void
  selectionMode?: boolean
}

const ClientesList: React.FC<ClientesListProps> = ({
  onClienteSelect,
  selectionMode = false
}) => {
  const { success, error: showError } = useToast()

  // Estado da lista
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [sortBy, setSortBy] = useState('nome')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  // Estado dos filtros
  const [filters, setFilters] = useState<ClienteFilters>({
    search: '',
    tipoCliente: undefined,
    distrito: undefined,
    ativo: true
  })
  const [showFilters, setShowFilters] = useState(false)

  // Estado dos modais
  const [selectedCliente, setSelectedCliente] = useState<Cliente | undefined>()
  const [showClienteModal, setShowClienteModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [clienteToDelete, setClienteToDelete] = useState<Cliente | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [clienteDetailsId, setClienteDetailsId] = useState<number | null>(null)

  // Carregar clientes
  const loadClientes = async () => {
    setIsLoading(true)
    try {
      const response = await clientesService.getClientes({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder
      })
      
      setClientes(response.data)
      setPagination(response.pagination)
    } catch (err: any) {
      showError('Erro ao carregar clientes', err.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadClientes()
  }, [pagination.page, pagination.limit, filters, sortBy, sortOrder])

  // Handlers
  const handleFilterChange = (field: keyof ClienteFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }))
  }

  const handleCreateCliente = () => {
    setSelectedCliente(undefined)
    setShowClienteModal(true)
  }

  const handleEditCliente = (cliente: Cliente) => {
    setSelectedCliente(cliente)
    setShowClienteModal(true)
  }

  const handleDeleteCliente = (cliente: Cliente) => {
    setClienteToDelete(cliente)
    setShowDeleteModal(true)
  }

  const handleViewDetails = (cliente: Cliente) => {
    setClienteDetailsId(cliente.id)
    setShowDetailsModal(true)
  }

  const confirmDelete = async () => {
    if (!clienteToDelete) return

    try {
      await clientesService.deleteCliente(clienteToDelete.id)
      success('Cliente removido com sucesso')
      loadClientes()
    } catch (err: any) {
      showError('Erro ao remover cliente', err.message)
    }
  }

  const handleClienteSave = (cliente: Cliente) => {
    loadClientes()
  }

  const handleRowClick = (cliente: Cliente) => {
    if (selectionMode && onClienteSelect) {
      onClienteSelect(cliente)
    }
  }

  // Colunas da tabela
  const columns = [
    {
      key: 'nome',
      title: 'Cliente',
      sortable: true,
      render: (value: string, cliente: Cliente) => (
        <div className="flex items-center space-x-3">
          <div className={`
            flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium
            ${cliente.tipoCliente === 'EMPRESA' ? 'bg-blue-500' : 'bg-green-500'}
          `}>
            {cliente.tipoCliente === 'EMPRESA' ? (
              <Building2 className="h-4 w-4" />
            ) : (
              <User className="h-4 w-4" />
            )}
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            {cliente.nomeComercial && (
              <div className="text-sm text-gray-500">{cliente.nomeComercial}</div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'tipoCliente',
      title: 'Tipo',
      sortable: true,
      render: (value: string) => (
        <span className={`
          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
          ${value === 'EMPRESA' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-green-100 text-green-800'
          }
        `}>
          {value === 'EMPRESA' ? 'Empresa' : 'Individual'}
        </span>
      )
    },
    {
      key: 'contacto',
      title: 'Contacto',
      render: (value: any, cliente: Cliente) => (
        <div className="space-y-1">
          {cliente.telefonePrincipal && (
            <div className="flex items-center text-sm text-gray-600">
              <Phone className="h-3 w-3 mr-1" />
              {cliente.telefonePrincipal}
            </div>
          )}
          {cliente.email && (
            <div className="flex items-center text-sm text-gray-600">
              <Mail className="h-3 w-3 mr-1" />
              {cliente.email}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'localizacao',
      title: 'Localização',
      render: (value: any, cliente: Cliente) => (
        <div className="text-sm text-gray-600">
          {cliente.cidade && cliente.distrito && (
            <div className="flex items-center">
              <MapPin className="h-3 w-3 mr-1" />
              {cliente.cidade}, {cliente.distrito}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'stats',
      title: 'Equipamentos',
      align: 'center' as const,
      render: (value: any, cliente: Cliente) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {cliente._count?.equipamentos || 0}
          </span>
        </div>
      )
    },
    {
      key: 'actions',
      title: 'Ações',
      align: 'center' as const,
      render: (value: any, cliente: Cliente) => (
        <div className="flex items-center justify-center space-x-2">
          <Button
            size="xs"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              handleViewDetails(cliente)
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              handleEditCliente(cliente)
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteCliente(cliente)
            }}
          >
            <Trash2 className="h-4 w-4 text-danger-600" />
          </Button>
        </div>
      )
    }
  ]

  const tipoClienteOptions = [
    { value: '', label: 'Todos os tipos' },
    { value: 'INDIVIDUAL', label: 'Pessoa Individual' },
    { value: 'EMPRESA', label: 'Empresa' }
  ]

  const distritoOptions = [
    { value: '', label: 'Todos os distritos' },
    ...DISTRITOS_PORTUGAL.map(distrito => ({
      value: distrito,
      label: distrito
    }))
  ]

  const ativoOptions = [
    { value: '', label: 'Todos' },
    { value: 'true', label: 'Ativos' },
    { value: 'false', label: 'Inativos' }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-gray-600">Gerir clientes individuais e empresas</p>
        </div>
        
        {!selectionMode && (
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
            >
              Exportar
            </Button>
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={handleCreateCliente}
            >
              Novo Cliente
            </Button>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Pesquisar por nome, email, telefone, NIF..."
              leftIcon={<Search className="h-4 w-4" />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Filter className="h-4 w-4" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <Select
              label="Tipo de Cliente"
              options={tipoClienteOptions}
              value={filters.tipoCliente || ''}
              onChange={(e) => handleFilterChange('tipoCliente', e.target.value || undefined)}
            />
            
            <Select
              label="Distrito"
              options={distritoOptions}
              value={filters.distrito || ''}
              onChange={(e) => handleFilterChange('distrito', e.target.value || undefined)}
            />
            
            <Select
              label="Estado"
              options={ativoOptions}
              value={filters.ativo === undefined ? '' : filters.ativo.toString()}
              onChange={(e) => {
                const value = e.target.value
                handleFilterChange('ativo', value === '' ? undefined : value === 'true')
              }}
            />
          </div>
        )}
      </div>

      {/* Tabela */}
      <Table
        data={clientes}
        columns={columns}
        keyField="id"
        isLoading={isLoading}
        emptyMessage="Nenhum cliente encontrado"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onRowClick={selectionMode ? handleRowClick : undefined}
        hoverable
        striped
      />

      {/* Paginação */}
      <Pagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Modais */}
      <ClienteModal
        isOpen={showClienteModal}
        onClose={() => setShowClienteModal(false)}
        cliente={selectedCliente}
        onSave={handleClienteSave}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Remover Cliente"
        message={`Tem certeza que deseja remover o cliente "${clienteToDelete?.nome}"? Esta ação não pode ser desfeita.`}
        confirmText="Remover"
        type="danger"
      />

      {/* Modal de Detalhes */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false)
          setClienteDetailsId(null)
        }}
        size="xl"
        title="Detalhes do Cliente"
      >
        {clienteDetailsId && (
          <ClienteDetails
            clienteId={clienteDetailsId}
            onEdit={() => {
              setShowDetailsModal(false)
              const cliente = clientes.find(c => c.id === clienteDetailsId)
              if (cliente) {
                handleEditCliente(cliente)
              }
            }}
          />
        )}
      </Modal>
    </div>
  )
}

export default ClientesList